// ============================================================================
// Supply Management — Purchase Order list
// Lists every PO the user can see (RLS handles scoping; superuser/exec/finance/
// AM all SELECT-allowed). Sorted by status (active first, draft/sent/shipped/
// delivered before invoiced/closed/cancelled), then most recent. Click a row
// to open the detail view.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import {
  ErrorState,
  PageHeader,
  StatusPill,
  UrgencyBadge,
} from '../../shared/components';
import type {
  PurchaseOrder,
  SupplyUrgency,
} from '../../shared/types';
import type { POStatus } from '../../shared/design-tokens';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';

const STATUS_RANK: Record<string, number> = {
  Draft: 0,
  Approved: 1,
  Sent: 2,
  Acknowledged: 3,
  Shipped: 4,
  Delivered: 5,
  Invoiced: 6,
  Closed: 7,
  Cancelled: 8,
};

interface POWithCount extends PurchaseOrder {
  line_count: number;
}

export default function POList() {
  const [rows, setRows] = useState<POWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('purchase_orders')
        .select(
          `po_id, po_date, requested_by_eid, requested_by_name, vendor_id, vendor_name,
           facility_id, facility_name, account_id, ship_to_address, urgency, status,
           approval_required, approved_by_eid, approval_date, submitted_date,
           expected_delivery, actual_delivery, subtotal, tax, shipping, total,
           payment_method, reimbursable, qbo_synced, invoice_number, notes,
           created_at, updated_at`,
        )
        .order('created_at', { ascending: false });

      if (e) throw e;
      const flatRows = (data ?? []) as unknown as PurchaseOrder[];

      const poIds = flatRows.map((r) => r.po_id);
      const lineCounts: Record<string, number> = {};

      if (poIds.length > 0) {
        const { data: lines } = await supabase
          .from('purchase_order_lines')
          .select('po_id')
          .in('po_id', poIds);
        for (const l of (lines ?? []) as Array<{ po_id: string }>) {
          lineCounts[l.po_id] = (lineCounts[l.po_id] ?? 0) + 1;
        }
      }

      const enriched = flatRows.map((r) => ({
        ...r,
        line_count: lineCounts[r.po_id] ?? 0,
      }));
      setRows(enriched);
    } catch (e) {
      console.error('[po-list] load failed', e);
      setError(errorMessage(e, 'Failed to load orders.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const sa = STATUS_RANK[a.status ?? 'Draft'] ?? 99;
      const sb = STATUS_RANK[b.status ?? 'Draft'] ?? 99;
      if (sa !== sb) return sa - sb;
      return (b.created_at ?? '').localeCompare(a.created_at ?? '');
    });
  }, [rows]);

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />
      <PageHeader
        title="Purchase orders"
        meta={loading ? 'Loading…' : `${rows.length} order${rows.length === 1 ? '' : 's'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* 2026-09-23 — a bulk purchase into stock locations (dialysis chemicals). */}
            <Link
              to="/supply/orders/new-stock"
              className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-surface px-3 py-1.5 text-sm font-medium text-tcs-text hover:bg-tcs-bg"
            >
              <Plus className="h-4 w-4" />
              Record a stock purchase
            </Link>
            <Link
              to="/supply/orders/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-tcs-inverse px-3 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              New PO
            </Link>
          </div>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={load} />
        </div>
      )}

      {!loading && sortedRows.length === 0 && (
        <div className="mt-4 rounded-md border border-tcs-border bg-tcs-surface p-6 text-center text-sm text-tcs-text-secondary">
          No purchase orders yet. Approve a supply request to create one.
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {sortedRows.map((r) => (
          <li key={r.po_id}>
            <Link
              to={`/supply/orders/${encodeURIComponent(r.po_id)}`}
              className="block rounded-md border border-tcs-border bg-tcs-surface p-4 transition-shadow hover:shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-tcs-text-secondary">
                      {r.po_id}
                    </span>
                    <StatusPill variant="po" value={(r.status as POStatus) ?? 'Draft'} />
                    {r.urgency && (
                      <UrgencyBadge tier={r.urgency as SupplyUrgency} />
                    )}
                  </div>
                  <div className="mt-2 text-sm font-medium text-tcs-text">
                    {r.vendor_name ?? r.vendor_id ?? 'No vendor'}
                  </div>
                  <div className="mt-1 text-xs text-tcs-text-secondary">
                    {r.line_count} line{r.line_count === 1 ? '' : 's'}
                    {r.facility_name ? ` · ${r.facility_name}` : ''}
                    {r.expected_delivery ? ` · expected ${r.expected_delivery}` : ''}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold text-tcs-text">
                    {r.total != null ? `$${Number(r.total).toFixed(2)}` : '—'}
                  </div>
                  {r.po_date && (
                    <div className="text-xs text-tcs-text-secondary">
                      {r.po_date}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
